// Used in login-routing.module.ts

// Angular
import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { FormHelper } from "#helpers/form.helper";
import { LocalStorage } from "#services/local-storage.service";
// RXJS/Store
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as fromStore from "../../store";

// Validators
import { EmailValidator } from "#validators/email.validator";
import { Auth, RegisterExistingStudentRequest, RegisterExistingTeacherRequest } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "login-onboard-existing-user",
    templateUrl: "./login-onboard-existing-user.component.html",
    styleUrls: ["./login-onboard-existing-user.component.scss"],
    standalone: false
})
export class LoginOnboardExistingUserComponent implements OnInit, OnDestroy {
    registerForm = this.fb.group({
        inviteHash: [
            { value: "", disabled: true },
            {
                validators: [Validators.required],
                updateOn: "change",
            },
        ],
        email: [
            { value: "", disabled: true },
            {
                validators: [Validators.required, EmailValidator.email],
                updateOn: "change",
            },
        ],
        password: [
            "",
            {
                validators: [Validators.required],
                updateOn: "change",
            },
        ],
    });
    hide = true; // Password visibility
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$: Observable<boolean>;
    bannerMessage$: Observable<string>;
    bannerColor$: Observable<string>;
    progressBar$?: Observable<boolean>;
    email = "";
    inviteHash = "";
    private ngUnsubscribe = new Subject();
    schoolInviteHash = "";
    courseInviteHash = "";

    constructor(
        private auth: Auth,
        private fb: UntypedFormBuilder,
        private localStorage: LocalStorage,
        private router: Router,
        public store: Store<fromStore.UserState>,
        public formHelper: FormHelper
    ) {
        this.bannerStatus$ = store.pipe(select(fromStore.getLoginBannerStatus));
        this.bannerMessage$ = store.pipe(
            select(fromStore.getLoginBannerMessage)
        );
        this.bannerColor$ = store.pipe(select(fromStore.getLoginBannerColor));
    }

    ngOnInit() {
        if (this.maintenanceMode) {
            this.store.dispatch(fromStore.userLoginMaintenance());
        } else {
            this.store.dispatch(fromStore.userLoginResetState());
        }

        this.store
            .pipe(select(fromStore.getAuthorized))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data === false) {
                    // you can only access this page through the register form
                    this.store.dispatch(fromStore.registerAuthFail());
                    this.router.navigate(["login/register"]);
                } else {
                    // user was redirected and entered a valid email address
                    this.progressBar$ = this.store.pipe(
                        select(fromStore.getRegisterProgressBar)
                    );
                    this.bannerStatus$ = this.store.pipe(
                        select(fromStore.getRegisterBannerStatus)
                    );
                    this.bannerMessage$ = this.store.pipe(
                        select(fromStore.getRegisterBannerMessage)
                    );
                    this.bannerColor$ = this.store.pipe(
                        select(fromStore.getRegisterBannerColor)
                    );

                    this.store
                        .pipe(select(fromStore.getEmail))
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((email) => {
                            this.email = email;
                            this.registerForm.get("email")?.setValue(email);
                        });

                    this.store
                        .pipe(select(fromStore.getInviteHash))
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((inviteHash) => {
                            this.inviteHash = inviteHash;
                            this.registerForm
                                .get("inviteHash")
                                ?.setValue(inviteHash);

                            const prefix = this.inviteHash.substring(0, 1);
                            if (prefix === "s") {
                                this.schoolInviteHash = this.inviteHash;
                            } else {
                                this.courseInviteHash = this.inviteHash;
                            }
                        });
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.registerForm, field, type);
    }

    register() {
        if (this.schoolInviteHash) {
            const payload: RegisterExistingTeacherRequest = {
                login: this.registerForm.get("email")?.value,
                password: this.registerForm.get("password")?.value,
                schoolInviteHash: this.registerForm.get("inviteHash")?.value,
            };

            this.store.dispatch(
                fromStore.registerFormAttemptExistingTeacher({
                    payload: payload,
                })
            );
        } else {
            const payload: RegisterExistingStudentRequest = {
                login: this.registerForm.get("email")?.value,
                password: this.registerForm.get("password")?.value,
                courseInviteHash: this.registerForm.get("inviteHash")?.value,
            };

            this.store.dispatch(
                fromStore.registerFormAttemptExistingStudent({
                    payload: payload,
                })
            );
        }
    }

    cancel() {
        this.router.navigate(["login/register"]);
    }

    goToSupportForm() {
        this.router.navigate(["support"]);
    }
}
